import { Sprite, Container, Texture } from "pixi.js";
import gsap from "gsap";

/**
 * Переиспользуемая покадровая «вспышка» — универсальный переходный эффект
 * показа попапа. Проигрывает последовательность кадров из resources по центру
 * (anchor 0.5); позиционируется и масштабируется снаружи.
 *
 * Кадры берутся по шаблону `${prefix}${i.padStart(5,"0")}`, напр. BG_00000.
 * На revealAt (доля прогресса) вызывает onReveal один раз и начинает гаснуть
 * (alpha 1→0) до конца — чтобы рассеяться и не перекрыть раскрытый попап.
 */
export class FrameFlash extends Container {
    constructor({ resources, prefix = "BG_", count = 51, fps = 30, blendMode } = {}) {
        super();
        this._fps = fps;
        this._frames = Array.from({ length: count }, (_, i) =>
            resources.get(`${prefix}${i.toString().padStart(5, "0")}`),
        ).filter(Boolean);

        this.sprite = new Sprite(this._frames[0] ?? Texture.EMPTY);
        this.sprite.anchor.set(0.5);
        if (blendMode) this.sprite.blendMode = blendMode;
        this.addChild(this.sprite);
    }

    /** Длительность полного прогона кадров в секундах. */
    get duration() {
        return this._frames.length / this._fps;
    }

    /**
     * Прогрев GPU: один раз отрендерить все кадры в почти невидимом виде,
     * чтобы первый показ анимации не тормозил. Требует, чтобы компонент уже
     * был в отрисовываемом дереве сцены — на время прогрева делаем его видимым.
     */
    async warmup() {
        if (!this._frames.length) return;

        const wasVisible = this.visible;
        const wasAlpha = this.alpha;
        this.visible = true;
        this.alpha = 0.001;

        const warm = new Container();
        this._frames.forEach((tex) => warm.addChild(new Sprite(tex)));
        this.addChild(warm);

        await new Promise((r) => requestAnimationFrame(r));

        warm.destroy({ children: true });
        this.visible = wasVisible;
        this.alpha = wasAlpha;
    }

    /** Сброс к исходному состоянию перед повторным показом. */
    reset() {
        this.alpha = 1;
        if (this._frames.length) this.sprite.texture = this._frames[0];
    }

    /**
     * Проиграть вспышку.
     * @param {number} revealAt доля прогресса [0..1], на которой зовётся onReveal
     *                          и начинается затухание вспышки.
     * @param {Function} onReveal колбэк показа попапа (вызывается один раз).
     * @returns {Promise} резолвится по завершении полного прогона кадров.
     */
    play({ revealAt = 0.7, onReveal } = {}) {
        if (!this._frames.length) {
            onReveal?.();
            return Promise.resolve();
        }

        const last = this._frames.length - 1;
        const revealFrame = revealAt * last;
        let revealed = false;
        const state = { frame: 0 };

        const tl = gsap.timeline();

        tl.to(state, {
            frame: last,
            duration: this.duration,
            ease: "none",
            onUpdate: () => {
                this.sprite.texture = this._frames[Math.round(state.frame)];
                if (!revealed && state.frame >= revealFrame) {
                    revealed = true;
                    onReveal?.();
                }
            },
        }, 0);

        // Кросс-фейд: вспышка рассеивается на отрезке revealAt → 1.0.
        tl.to(this, {
            alpha: 0,
            duration: this.duration * (1 - revealAt),
            ease: "sine.in",
        }, this.duration * revealAt);

        return tl.then();
    }
}
